import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';

interface BarPreviewPopupProps {
  slug: string;
  href: string;
  children: React.ReactNode;
}

const PLACEHOLDER_GRADIENT = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)';

export function BarPreviewPopup({ slug, href, children }: BarPreviewPopupProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [above, setAbove] = useState(true);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const { data } = useQuery({
    queryKey: ['bar-preview', slug],
    queryFn: () => barsApi.getById(slug),
    enabled: isHovering,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const bar = data?.bar;

  const updatePosition = useCallback(() => {
    if (!linkRef.current) return;
    const rect = linkRef.current.getBoundingClientRect();
    // Show above if there's enough space, otherwise below
    setAbove(rect.top > 200);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
    hoverTimerRef.current = setTimeout(() => {
      updatePosition();
      setShowPopup(true);
    }, 300);
  }, [updatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsHovering(false);
    setShowPopup(false);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Get initials for placeholder
  const initials = bar
    ? bar.name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '';

  return (
    <span
      className="relative inline"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link
        ref={linkRef}
        to={href}
        className="text-accent-primary hover:underline"
      >
        {children}
      </Link>

      {showPopup && bar && (
        <span
          className={`absolute left-1/2 z-50 hidden md:block ${above ? 'bottom-full mb-2' : 'top-full mt-2'}`}
          style={{
            transform: 'translateX(-50%)',
            animation: 'barPreviewFadeIn 150ms ease-out',
          }}
        >
          <span
            className="block w-64 overflow-hidden rounded-lg shadow-lg border border-[var(--color-border)]"
            style={{ background: 'var(--color-card)' }}
          >
            {/* Faded logo background */}
            <span className="relative block h-24 overflow-hidden" style={{ background: '#1a1a2e' }}>
              {bar.logoUrl ? (
                <img
                  src={bar.logoUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ opacity: 0.3 }}
                />
              ) : (
                <span
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ background: PLACEHOLDER_GRADIENT }}
                >
                  <span className="text-3xl font-bold text-white/20">{initials}</span>
                </span>
              )}
              {/* Gradient overlay */}
              <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

              {/* Bar name over the image */}
              <span className="absolute bottom-0 left-0 right-0 p-3">
                <span className="block text-sm font-bold text-white leading-tight">{bar.name}</span>
                {bar.address && (
                  <span className="block text-xs text-white/70 mt-0.5 truncate">{bar.address}</span>
                )}
              </span>
            </span>
          </span>
        </span>
      )}

      <style>{`
        @keyframes barPreviewFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(${above ? '4px' : '-4px'}); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </span>
  );
}
